import { useTranslation } from "react-i18next";
import type { TellescopeFormFieldBaseProps } from "../types";
import { useMemo, useState } from "react";
import {
  Button,
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useDebouncedValue,
  useStateWithSetAction,
} from "@tellescope/ui";
import { type TellescopeModelFromName, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { handleCustomFilter } from "../utils";
import { Check, ChevronsUpDown } from "lucide-react";

export interface AllergiesFieldProps extends TellescopeFormFieldBaseProps<"Allergies"> {
  allergies?: TellescopeModelFromName<"allergy_codes">[];
  customFilter?: (
    searchTerm: string,
    allergies: TellescopeModelFromName<"allergy_codes">[]
  ) => TellescopeModelFromName<"allergy_codes">[];
}

export const AllergiesField = ({
  title,
  value,
  onChange,
  className,
  allergies,
  customFilter,
  onBlur,
  error,
}: AllergiesFieldProps) => {
  const { t } = useTranslation("forms");
  const [open, setOpen] = useStateWithSetAction(false, (isOpen) => {
    if (!isOpen) {
      onBlur?.();
    }
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery);
  const allergiesQuery = useTellescopeInfiniteQuery({
    model: "allergy_codes",
    method: "getSome",
    request: {
      search:
        debouncedSearchQuery && !customFilter
          ? {
              query: debouncedSearchQuery,
            }
          : undefined,
    },
    select: (data) => data.pages.flat(),
    enabled: allergies === undefined,
  });

  const filteredAllergies = useMemo(
    () =>
      handleCustomFilter({
        filterOptions: allergies,
        customFilter,
        debouncedSearchQuery,
        fallbackOptions: allergiesQuery?.data ?? [],
        getStrings: (item) => [item.code, item.display],
      }),
    [allergies, allergiesQuery?.data, customFilter, debouncedSearchQuery]
  );

  return (
    <div className="space-y-1">
      {!!title && <h4>{title}</h4>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild className={className}>
          <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal">
            {value?.length ? (
              <div className="flex flex-col gap-1 items-start overflow-hidden w-full text-left">
                <div className="font-medium text-sm truncate w-full">
                  {value.map((value) => value.display).join(", ")}
                </div>
              </div>
            ) : (
              <span className="text-muted-foreground">{t("Choose allergies...")}</span>
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              value={searchQuery}
              onValueChange={setSearchQuery}
              placeholder={t("Search allergies...")}
              className="h-9"
            />
            <CommandList>
              <CommandEmpty>
                {allergiesQuery.isLoading && allergies?.length === undefined
                  ? t("Loading allergies...")
                  : t("No allergies found")}
              </CommandEmpty>
              <CommandGroup>
                {filteredAllergies?.map((allergy) => {
                  const isSelected = !!value?.some((item) => item.code === allergy.code);
                  return (
                    <CommandItem
                      key={allergy.id}
                      value={allergy.code}
                      onSelect={() => {
                        if (isSelected) {
                          onChange(value?.filter((item) => item.code !== allergy.code) ?? []);
                        } else {
                          onChange([
                            ...(value ?? []),
                            { code: allergy.code, display: allergy.display, system: allergy.system },
                          ]);
                        }
                      }}
                    >
                      <div className="flex flex-col gap-1 w-full">
                        <div className="font-medium text-sm truncate">{allergy.display}</div>
                        <div className="text-xs text-muted-foreground truncate">{allergy.code}</div>
                      </div>
                      <Check className={cn("ml-auto h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
